import React from 'react';
import { VaultKeTheme } from '../utils/theme';

const AuthTabBar: React.FC = () => {
  // Use window.location for navigation since we're outside router context
  const [currentPath, setCurrentPath] = React.useState(window.location.pathname);
  const currentPathRef = React.useRef(currentPath);

  // Listen for navigation changes
  React.useEffect(() => {
    const handleNavigation = () => {
      const newPath = window.location.pathname;
      setCurrentPath(newPath);
      currentPathRef.current = newPath;
    };

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handleNavigation);

    // Listen for any location changes by polling
    const interval = setInterval(() => {
      const newPath = window.location.pathname;
      if (newPath !== currentPathRef.current) {
        setCurrentPath(newPath);
        currentPathRef.current = newPath;
      }
    }, 100);

    return () => {
      window.removeEventListener('popstate', handleNavigation);
      clearInterval(interval);
    };
  }, []);

  const styles: { [key: string]: React.CSSProperties } = {
    tabBar: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: VaultKeTheme.colors.surface,
      borderTop: `1px solid ${VaultKeTheme.colors.border}`,
      padding: `${VaultKeTheme.spacing.sm} ${VaultKeTheme.spacing.md}`,
      display: 'flex',
      flexDirection: 'row',
      height: '70px',
      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
      zIndex: 1000,
    },
    tabItem: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: VaultKeTheme.spacing.xs,
      paddingBottom: VaultKeTheme.spacing.xs,
    },
    tabButton: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '44px',
      paddingLeft: VaultKeTheme.spacing.xs,
      paddingRight: VaultKeTheme.spacing.xs,
      cursor: 'pointer',
      borderRadius: VaultKeTheme.borderRadius.sm,
      transition: 'all 0.2s ease',
      padding: VaultKeTheme.spacing.sm,
    },
    tabButtonActive: {
      backgroundColor: VaultKeTheme.colors.primary + '15',
    },
    tabIcon: {
      fontSize: '20px',
      marginBottom: VaultKeTheme.spacing.xs,
    },
    tabLabel: {
      fontSize: VaultKeTheme.typography.caption.fontSize,
      fontWeight: '500',
      textAlign: 'center',
    },
    activeText: {
      color: VaultKeTheme.colors.primary,
    },
    inactiveText: {
      color: VaultKeTheme.colors.textSecondary,
    },
  };

  // Smart shortcuts for auth screens - exactly 3 icons
  const quickShortcuts = [
    {
      name: 'login',
      label: 'Login',
      icon: '🔐',
      path: '/login'
    },
    {
      name: 'register',
      label: 'Sign Up',
      icon: '👤',
      path: '/register'
    },
    {
      name: 'forgot-password',
      label: 'Reset',
      icon: '🔑',
      path: '/forgot-password'
    },
  ];

  const isAuthPage = (pathname: string) => {
    const normalizedPath = pathname.replace(/\/$/, ''); // Remove trailing slash
    return ['/login', '/register', '/forgot-password', '/reset-password', '/email-verification', '/terms-of-service', '/privacy-policy'].includes(normalizedPath);
  };

  // Only show tab bar on auth pages
  if (!isAuthPage(currentPath)) {
    return null;
  }

  return (
    <div style={styles.tabBar}>
      {quickShortcuts.map((item) => {
        const isActive = currentPath === item.path;

        return (
          <div
            key={item.name}
            style={styles.tabItem}
            onClick={() => {
              window.location.href = item.path;
            }}
          >
            <div
              style={{
                ...styles.tabButton,
                ...(isActive ? styles.tabButtonActive : {}),
              }}
            >
              <div style={styles.tabIcon}>{item.icon}</div>
              <div
                style={{
                  ...styles.tabLabel,
                  ...(isActive ? styles.activeText : styles.inactiveText),
                }}
              >
                {item.label}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AuthTabBar;